import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Constant } from '@varos/common-constant';
import qs from 'qs';
import moment from 'moment';
import { ErrorResponseSchema, ApiErrorResponseSchema } from '../../api';
import { ApiError, Authenticator, ServiceUnavailableError } from '../../base';
import { AxiosConfig } from './axiosConfig';
import { z } from 'zod';
import { LocalStorageKeys } from '../../config';

export function createAxios(config: AxiosConfig) {
    const instance = axios.create({
        ...config.defaults,
    });
    const serializeDates = (obj: any): any => {
        if (obj instanceof Date) {
            // Check if the Date has time components
            if (
                obj.getHours() === 0 &&
                obj.getMinutes() === 0 &&
                obj.getSeconds() === 0 &&
                obj.getMilliseconds() === 0
            ) {
                // Date has no time component, format as 'YYYY-MM-DD'
                return moment(obj).format('YYYY-MM-DD');
            } else {
                // Date has time component, format including time
                return moment(obj).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
            }
        } else if (Array.isArray(obj)) {
            return obj.map((item) => serializeDates(item));
        } else if (obj !== null && typeof obj === 'object') {
            const newObj: any = {};
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    newObj[key] = serializeDates(obj[key]);
                }
            }
            return newObj;
        } else {
            return obj;
        }
    };

    const PromptVersionSchema = z
        .string()
        .transform((item) => (item ? JSON.parse(item) : null))
        .pipe(z.enum(['develop', 'active', 'preview']).nullable());

    instance.interceptors.request.use((request) => {
        const promptVersion = window.localStorage.getItem(
            LocalStorageKeys.PROMPT_VERSION
        );
        const parsed = PromptVersionSchema.safeParse(promptVersion);
        if (!parsed.success) {
            console.log('invalid prompt version', promptVersion);
            return request;
        }
        if (parsed.data === null) {
            return request;
        }
        // console.log('setting prompt version header', parsed.data);
        request.headers[Constant.Prompt.VERSION_HEADER_NAME] = parsed.data;
        return request;
    });

    instance.interceptors.response.use(
        (response) => {
            return response;
        },
        (axiosError: AxiosError) => {
            if (
                axiosError.status &&
                axiosError.status >= 502 &&
                axiosError.status <= 504
            ) {
                throw new ServiceUnavailableError(axiosError);
            }

            // Try to parse with the new ErrorResponseSchema first
            const newParsed = ErrorResponseSchema.safeParse(
                axiosError.response?.data ?? {}
            );

            if (newParsed.success) {
                // Handle new error format
                const errorInfo = {
                    name: newParsed.data.error,
                    description:
                        newParsed.data.description ||
                        newParsed.data.title ||
                        'An error occurred',
                    hint: newParsed.data.hint,
                    title: newParsed.data.title,
                    error: newParsed.data.error,
                    retryable: newParsed.data.retryable,
                    details: newParsed.data.details,
                };

                if (axiosError.response?.status === 404) {
                    const error = new ApiError(
                        newParsed.data.title || newParsed.data.error,
                        axiosError,
                        errorInfo
                    );
                    throw error;
                }
                if (axiosError.response?.status === 403) {
                    const error = new ApiError(
                        newParsed.data.title ||
                            'You do not have permission to perform this action',
                        axiosError,
                        errorInfo
                    );
                    throw error;
                }
                // For all other status codes with new format
                const error = new ApiError(
                    newParsed.data.title || newParsed.data.error,
                    axiosError,
                    errorInfo
                );
                throw error;
            }

            // Fall back to existing ApiErrorResponseSchema for backwards compatibility
            const oldParsed = ApiErrorResponseSchema.safeParse(
                axiosError.response?.data ?? {}
            );

            if (oldParsed.success) {
                // Handle old error format
                if (axiosError.response?.status === 404) {
                    const error = new ApiError(oldParsed.data.detail, axiosError);
                    throw error;
                }
                if (axiosError.response?.status === 403) {
                    const error = new ApiError(
                        'You do not have permission to perform this action',
                        axiosError
                    );
                    throw error;
                }
                // For all other status codes with old format
                const error = new ApiError(oldParsed.data.detail, axiosError);
                throw error;
            }

            // Final fallback for unrecognized error formats
            throw new ApiError(axiosError.message, axiosError);
        }
    );

    // Add the request interceptor to inject the auth token and serialize dates
    instance.interceptors.request.use(async (requestConfig) => {
        // 1) Grab the token from our custom config
        const token = await config.getToken();

        // 2) If we have a token, inject the Authorization header (only if not already set)
        if (token) {
            requestConfig.headers = requestConfig.headers ?? {};
            if (!requestConfig.headers.Authorization) {
                requestConfig.headers.Authorization = `Bearer ${token}`;
            }
        }

        // 3) Serialize dates in params
        if (requestConfig.params) {
            requestConfig.params = serializeDates(requestConfig.params);
        }
        // 4) Serialize dates in data (request body)
        if (
            requestConfig.data &&
            Object.getPrototypeOf(requestConfig.data) !== FormData.prototype
        ) {
            requestConfig.data = serializeDates(requestConfig.data);
        }

        // Apply artificial delay if configured and in development
        if (process.env.NODE_ENV === 'development' && config.artificialDelayMs) {
            let delayMs = 0;
            if (typeof config.artificialDelayMs === 'number') {
                delayMs = config.artificialDelayMs;
            } else if (
                Array.isArray(config.artificialDelayMs) &&
                config.artificialDelayMs.length === 2
            ) {
                const [min, max] = config.artificialDelayMs;
                delayMs = Math.random() * (max - min) + min;
            }

            if (delayMs > 0) {
                await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
        }

        return requestConfig;
    });

    return instance;
}
